import React, { useEffect, useState } from "react";
import { Modal, Typography, Button, Tag, Space, Card, Table } from "antd";
import {
  CloseOutlined,
  EyeOutlined,
  FileTextOutlined,
  MessageOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import {
  errorMessage,
  successMessage,
} from "../../../../utils/notificationUtils";
import { QueryService } from "../../../../apis/PreAuthQueryService";
import TextArea from "antd/es/input/TextArea";
import HistoryModal from "./HistoryModal";

const { Text } = Typography;

interface ViewQueryModalProps {
  visible: boolean;
  onClose: () => void;
  viewedQuery?: any;
  handleOpenUploadedDocument: any;
  handleGetData: any;
}

const ViewQueryModal: React.FC<ViewQueryModalProps> = ({
  visible,
  onClose,
  viewedQuery,
  handleOpenUploadedDocument,
  handleGetData,
}) => {
  const { auth } = useSelector((state: RootState) => state);
  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [remarks, setRemarks] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [history, setHistory] = useState<any>([]);
  const [historyModal, setHistoryModal] = useState<boolean>(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  const [parentQuery, setParentQuery] = useState<any>(null);
  const [returnedResponse, setReturnedResponse] = useState<any>(null);

  useEffect(() => {
    setReturnedResponse(viewedQuery?.returnedResponse);
  }, [viewedQuery]);

  const handleGetHistory = async () => {
    setHistoryModal(true);
    try {
      setIsLoadingHistory(true);
      const res = await QueryService.getQueryHistory(
        viewedQuery?.id || viewedQuery?.queryId,
      );
      setHistory(res);
    } catch (err) {
      errorMessage("Please, Try again after sometime");
      setHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleGetHistoryParentQuery = async () => {
    try {
      setIsLoadingHistory(true);
      const res = await QueryService.getQueryHistory(
        viewedQuery?.id || viewedQuery?.queryId,
      );
      if (res?.length > 0) {
        const queryData = JSON.parse(res?.[0]?.payloadJson)?.query;
        setParentQuery(queryData.queryQuestion || queryData.receivedQuery);
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
      setHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (visible) handleGetHistoryParentQuery();
  }, [visible]);

  const handleReplyQuery = async () => {
    try {
      const payload = {
        approved: false,
        followUpQueryText: remarks,
      };
      setIsLoading(true);
      await QueryService.payerReplyQuery(
        viewedQuery?.casesNewId,
        viewedQuery?.visitInfoId,
        viewedQuery?.id || viewedQuery?.queryId,
        payload,
      );
      successMessage("Payer Query Replied Successfully");
      setTimeout(() => {
        onClose();
      }, 0);
      handleGetData();
      setIsReplying(false);
    } catch (err) {
      errorMessage("Please, Try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitToPortal = async () => {
    try {
      const payload = {
        approved: true,
        submissionRemark: returnedResponse
      };
      setIsSubmitting(true);
      await QueryService.payerReplyQuery(
        viewedQuery?.casesNewId,
        viewedQuery?.visitInfoId,
        viewedQuery?.id || viewedQuery?.queryId,
        payload,
      );
      successMessage("Payer Query Submitted Successfully");
      setTimeout(() => {
        onClose();
      }, 0);
      handleGetData();
      setIsReplying(false);
    } catch (err) {
      errorMessage("Please, Try again later");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!visible) {
      setIsReplying(false);
    }
  }, [visible]);

  return (
    <>
      <Modal
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
            }}
          >
            <Space>
              <FileTextOutlined style={{ color: "#00549e", fontSize: 18 }} />
              <span style={{ fontSize: 16, fontWeight: 600, color: "#00549e" }}>
                QUERY DETAILS
              </span>
            </Space>
            {viewedQuery?.typeOfQuery === "Payer" && (
              <Button
                type="link"
                icon={<HistoryOutlined />}
                onClick={handleGetHistory}
                style={{
                  color: "#00549e",
                  fontWeight: 500,
                  padding: 0,
                }}
              >
                View History
              </Button>
            )}
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={1100}
        className="create-query-modal"
        closeIcon={<CloseOutlined style={{ background: "white" }} />}
        style={{
          borderRadius: 12,
          overflow: "auto",
          top: 2,
          left: 4,
          padding: 0,
        }}
        bodyStyle={{
          // padding: "24px",
          background: "#fafafa",
          overflow: "auto",
          height: "130vh",
        }}
      >
        {/* Query Request Section */}
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            border: "1px solid #e5e7eb",
          }}
          bodyStyle={{ padding: "20px" }}
        >
          {parentQuery && (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
                color: "#334155",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div>
                  <Text strong style={{ color: "#1e293b" }}>
                    Main Payer Query:
                  </Text>
                  <div>{parentQuery}</div>
                </div>
              </div>
            </div>
          )}
          <Space align="center" style={{ marginBottom: 16 }}>
            <MessageOutlined style={{ color: "#ef4444", fontSize: 16 }} />
            <Text strong style={{ fontSize: 14, color: "#374151" }}>
              QUERY REQUEST
            </Text>
          </Space>
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 6,
              padding: 16,
              marginBottom: 16,
              color: "#dc2626",
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <Text strong style={{ color: "#dc2626" }}>
              REMARKS
            </Text>
            {/* <div style={{ marginTop: 8 }}>{parentQuery || viewedQuery?.receivedQuery}</div> */}
            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {
                // <div>
                //   <Text strong style={{ color: "#dc2626" }}>
                //     Received Query:
                //   </Text>
                <div>{viewedQuery?.receivedQuery}</div>
                // </div>
              }
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <Text style={{ fontSize: 13, color: "#6b7280" }}>
              Type of Query:
            </Text>
            <Tag color="blue" style={{ fontSize: 12, padding: "2px 8px" }}>
              {viewedQuery?.typeOfQuery}
            </Tag>
          </div>

          <Table
            columns={[
              {
                title: "Document Name",
                dataIndex: "name",
                key: "name",
                render: (text) => (
                  <Text style={{ fontWeight: 500, color: "#374151" }}>
                    {text}
                  </Text>
                ),
              },
              {
                title: "Remarks",
                dataIndex: "remarks",
                key: "remarks",
                render: (text) => (
                  <Text style={{ color: "#6b7280" }}>
                    {text || "No remarks"}
                  </Text>
                ),
              },
              {
                title: "Action",
                key: "action",
                width: 100,
                render: (text, record) => {
                  const matchingDocuments =
                    viewedQuery?.returnedDocumentUrl?.filter(
                      (doc: any) => doc.documentLabelType === record.name,
                    );
                  return matchingDocuments?.length > 0 ? (
                    <Button
                      icon={<EyeOutlined />}
                      size="small"
                      onClick={() => {
                        handleOpenUploadedDocument(record?.name);
                      }}
                      style={{
                        borderColor: "#059669",
                        color: "#059669",
                        borderRadius: 6,
                      }}
                    >
                      View
                    </Button>
                  ) : null;
                },
              },
            ]}
            dataSource={viewedQuery?.queryRequiredDocuments}
            pagination={false}
            size="middle"
            style={{
              borderRadius: 6,
              overflow: "hidden",
            }}
          />
        </Card>

        {/* Query Response Section */}
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            border: "1px solid #e5e7eb",
          }}
          bodyStyle={{ padding: "20px" }}
        >
          <Space align="center" style={{ marginBottom: 16 }}>
            <MessageOutlined style={{ color: "#00549e", fontSize: 16 }} />
            <Text strong style={{ fontSize: 14, color: "#374151" }}>
              QUERY RESPONSE
            </Text>
          </Space>
          {auth?.role?.role === "FO_STAFF" ? (
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 6,
                padding: 16,
                color: "#166534",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {viewedQuery?.returnedResponse}
            </div>
          ) : (
            <div>
              {/* <Space align="center" style={{ marginBottom: 16 }}>
              <MessageOutlined style={{ color: "#f59e0b", fontSize: 16 }} />
              <Text strong style={{ fontSize: 14, color: "#374151" }}>
                RESPONSE REMARKS
              </Text>
            </Space> */}
              <TextArea
                // defaultValue={viewedQuery?.returnedResponse}
                disabled={viewedQuery?.stateName?.toLowerCase()?.includes("submitted")}
                value={returnedResponse}
                onChange={(e) => setReturnedResponse(e.target.value)}
                rows={4}
                placeholder="Enter your response remark"
                style={{
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  resize: "none",
                }}
              />
            </div>
          )}
        </Card>

        {/* Remarks Section (when replying) */}
        {isReplying && (
          <Card
            style={{
              marginBottom: 24,
              borderRadius: 8,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: "1px solid #e5e7eb",
            }}
            bodyStyle={{ padding: "20px" }}
          >
            <Space align="center" style={{ marginBottom: 16 }}>
              <MessageOutlined style={{ color: "#f59e0b", fontSize: 16 }} />
              <Text strong style={{ fontSize: 14, color: "#374151" }}>
                RESPONSE REMARKS
              </Text>
            </Space>
            <TextArea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={4}
              placeholder="Enter your response remark"
              style={{
                borderRadius: 6,
                border: "1px solid #d1d5db",
                resize: "none",
              }}
            />
          </Card>
        )}

        {/* Action Buttons */}
        {!viewedQuery?.stateName?.toLowerCase()?.includes("submitted") &&
        viewedQuery?.typeOfQuery === "Payer" ? (
          !isReplying ? (
            auth?.role?.role !== "FO_STAFF" ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 16,
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <Button
                  type="primary"
                  onClick={() => setIsReplying(true)}
                  size="large"
                  style={{
                    backgroundColor: "#00549e",
                    borderColor: "#00549e",
                    borderRadius: 8,
                    fontWeight: 500,
                    padding: "8px 24px",
                    boxShadow: "0 2px 4px rgba(0,84,158,0.2)",
                  }}
                >
                  Reply
                </Button>
                <Button
                  type="primary"
                  onClick={handleSubmitToPortal}
                  loading={isSubmitting}
                  size="large"
                  style={{
                    backgroundColor: "#dc2626",
                    borderColor: "#dc2626",
                    borderRadius: 8,
                    fontWeight: 500,
                    padding: "8px 24px",
                    boxShadow: "0 2px 4px rgba(220,38,38,0.2)",
                  }}
                >
                  Submit
                </Button>
              </div>
            ) : null
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 16,
                marginTop: 20,
                paddingTop: 16,
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <Button
                type="primary"
                onClick={handleReplyQuery}
                loading={isLoading}
                size="large"
                style={{
                  backgroundColor: "#00549e",
                  borderColor: "#00549e",
                  borderRadius: 8,
                  fontWeight: 500,
                  padding: "8px 24px",
                  boxShadow: "0 2px 4px rgba(0,84,158,0.2)",
                }}
              >
                Reply Query
              </Button>
            </div>
          )
        ) : (
          <></>
        )}
      </Modal>

      <HistoryModal
        visible={historyModal}
        onClose={() => setHistoryModal(false)}
        data={history || []}
        isLoading={isLoadingHistory}
      />
    </>
  );
};

export default ViewQueryModal;
